import React, { useState, useEffect } from 'react';
import axios from 'axios';
import useAutoRefresh from '@/hooks/useAutoRefresh';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  GraduationCap, Search, Plus, Edit, Trash2, Eye, Loader2,
  BookOpen, IndianRupee, Clock, Users, RefreshCw, Check, X,
  Filter, ToggleLeft, ToggleRight, Upload, Download, FileSpreadsheet
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to extract years from duration string
const extractYears = (duration) => {
  if (!duration) return '';
  const match = duration.match(/(\d+)\s*year/i);
  return match ? match[1] : '';
};

const AdminCoursesPage = () => {
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState(null); // 'activate' | 'deactivate' | 'delete' | null

  // Bulk upload (CSV/Excel)
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [upsertMode, setUpsertMode] = useState(false);
  
  // Dialog states
  const [courseDialogOpen, setCourseDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Form state
  const [courseForm, setCourseForm] = useState({
    name: '',
    description: '',
    duration: '',
    fee: '',
    yearly_fee: '',
    duration_years: '',
    course_type: 'paid',
    delivery_mode: 'offline',
    category: 'professional',
    training_mode: 'Both', // STT | RPL | Both — only meaningful for professional
    is_active: true,
    eligibility: '',
    minimum_qualification: '10th',
    subjects: []
  });

  // Check if current category is distance learning (degree/diploma)
  const isDistanceLearning = courseForm.category === 'distance_learning' || 
                              courseForm.category === 'degree' || 
                              courseForm.category === 'diploma';

  // Calculate total fee for distance learning courses
  const calculatedTotalFee = isDistanceLearning && courseForm.yearly_fee && courseForm.duration_years
    ? parseFloat(courseForm.yearly_fee) * parseInt(courseForm.duration_years)
    : 0;

  useEffect(() => {
    fetchCourses();
  }, []);

  // Auto-refresh on tab focus / regain visibility
  useAutoRefresh(() => fetchCourses());

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/courses`);
      setCourses(response.data || []);
    } catch (error) {
      console.error('Failed to fetch courses:', error);
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCourse = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      // Prepare data - for distance learning, use yearly_fee and calculate total
      const isDistanceCategory = courseForm.category === 'distance_learning' || 
                                  courseForm.category === 'degree' || 
                                  courseForm.category === 'diploma';
      
      const courseData = {
        ...courseForm,
        // For distance learning, fee = yearly_fee, total_fee = calculated
        fee: isDistanceCategory ? parseFloat(courseForm.yearly_fee || 0) : parseFloat(courseForm.fee || 0),
        yearly_fee: isDistanceCategory ? parseFloat(courseForm.yearly_fee || 0) : null,
        duration_years: isDistanceCategory ? parseInt(courseForm.duration_years || 1) : null,
        total_fee: isDistanceCategory ? parseFloat(courseForm.yearly_fee || 0) * parseInt(courseForm.duration_years || 1) : null,
        fee_type: isDistanceCategory ? 'yearly' : 'total',
        // Training mode is only meaningful for Professional courses
        training_mode: courseForm.category === 'professional' ? (courseForm.training_mode || 'Both') : null
      };
      
      if (isEditing && selectedCourse) {
        await axios.put(`${API}/admin/courses/${selectedCourse.id}`, courseData);
        toast.success('Course updated successfully');
      } else {
        await axios.post(`${API}/admin/courses`, courseData);
        toast.success('Course created successfully');
      }
      setCourseDialogOpen(false);
      resetForm();
      fetchCourses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save course');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteCourse = async () => {
    if (!selectedCourse) return;
    try {
      await axios.delete(`${API}/admin/courses/${selectedCourse.id}`);
      toast.success('Course deleted successfully');
      setDeleteConfirmOpen(false);
      setSelectedCourse(null);
      fetchCourses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete course');
    }
  };

  const handleToggleActive = async (course) => {
    try {
      await axios.put(`${API}/admin/courses/${course.id}`, {
        ...course,
        is_active: !course.is_active
      });
      toast.success(`Course ${course.is_active ? 'deactivated' : 'activated'}`);
      fetchCourses();
    } catch (error) {
      toast.error('Failed to update course status');
    }
  };

  // ===== Bulk actions =====
  const filteredIds = () => filteredCourses.map((c) => c.id);

  const toggleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAllVisible = () => {
    const ids = filteredIds();
    const allSelected = ids.length > 0 && ids.every((id) => selectedIds.includes(id));
    setSelectedIds(allSelected ? selectedIds.filter((id) => !ids.includes(id)) : Array.from(new Set([...selectedIds, ...ids])));
  };

  const clearSelection = () => setSelectedIds([]);

  const runBulkAction = async (action) => {
    if (selectedIds.length === 0) return;
    setBulkBusy(true);
    let success = 0;
    let failed = 0;
    try {
      const tasks = selectedIds.map(async (id) => {
        const course = courses.find((c) => c.id === id);
        if (!course) {
          failed += 1;
          return;
        }
        try {
          if (action === 'activate' || action === 'deactivate') {
            await axios.put(`${API}/admin/courses/${id}`, {
              ...course,
              is_active: action === 'activate',
            });
          } else if (action === 'delete') {
            await axios.delete(`${API}/admin/courses/${id}`);
          }
          success += 1;
        } catch (e) {
          console.error('Bulk action failed for', id, e);
          failed += 1;
        }
      });
      await Promise.all(tasks);
      if (success > 0) {
        const verb = action === 'activate' ? 'activated' : action === 'deactivate' ? 'deactivated' : 'deleted';
        toast.success(`${success} course${success === 1 ? '' : 's'} ${verb}${failed ? ` • ${failed} failed` : ''}`);
      } else if (failed > 0) {
        toast.error(`Failed to ${action} ${failed} course${failed === 1 ? '' : 's'}`);
      }
      clearSelection();
      setBulkConfirm(null);
      fetchCourses();
    } finally {
      setBulkBusy(false);
    }
  };

  // ===== Bulk CSV/Excel upload =====
  const downloadTemplate = async (format = 'csv') => {
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/courses/template?format=${format}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = format === 'xlsx' ? 'courses_template.xlsx' : 'courses_template.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (e) {
      toast.error('Failed to download template');
    }
  };

  const exportCourses = async (format = 'csv') => {
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/courses/export?format=${format}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = format === 'xlsx' ? 'courses_export.xlsx' : 'courses_export.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`${courses.length} courses exported`);
    } catch (e) {
      toast.error('Failed to export courses');
    }
  };

  const handleBulkUpload = async () => {
    if (!uploadFile) {
      toast.error('Please select a CSV or Excel file');
      return;
    }
    setUploading(true);
    setUploadResult(null);
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', uploadFile);
      const mode = upsertMode ? 'upsert' : 'create';
      const res = await axios.post(`${API}/admin/courses/bulk-upload?mode=${mode}`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUploadResult(res.data);
      const changes = (res.data.added || 0) + (res.data.updated || 0);
      if (changes > 0) {
        toast.success(res.data.message);
        fetchCourses();
      } else {
        toast.warning(res.data.message);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || 'Upload failed';
      toast.error(msg);
      setUploadResult({ added: 0, updated: 0, skipped: 0, errors: [msg] });
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setCourseForm({
      name: '',
      description: '',
      duration: '',
      fee: '',
      yearly_fee: '',
      duration_years: '',
      course_type: 'paid',
      delivery_mode: 'offline',
      category: 'professional',
      training_mode: 'Both',
      is_active: true,
      eligibility: '',
      minimum_qualification: '10th',
      subjects: []
    });
    setIsEditing(false);
    setSelectedCourse(null);
  };

  const openEditDialog = (course) => {
    setSelectedCourse(course);
    setCourseForm({
      name: course.name || '',
      description: course.description || '',
      duration: course.duration || '',
      fee: course.fee || '',
      yearly_fee: course.yearly_fee || course.fee || '',
      duration_years: course.duration_years || extractYears(course.duration) || '',
      course_type: course.course_type || 'paid',
      delivery_mode: course.delivery_mode || 'offline',
      category: course.category || 'professional',
      training_mode: course.training_mode || 'Both',
      is_active: course.is_active !== false,
      eligibility: course.eligibility || '',
      minimum_qualification: course.minimum_qualification || '10th',
      subjects: course.subjects || []
    });
    setIsEditing(true);
    setCourseDialogOpen(true);
  };

  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || course.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-courses-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Courses Management</h1>
          <p className="text-slate-500 mt-1">{courses.length} total courses</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button onClick={fetchCourses} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
          <Button
            onClick={() => exportCourses('csv')}
            variant="outline"
            className="gap-2 border-purple-200 text-purple-700 hover:bg-purple-50"
            data-testid="export-courses-csv"
            title="Download all courses as CSV (for bulk-edit, then re-upload with Update mode)"
          >
            <Download className="w-4 h-4" />
            Export
          </Button>
          <Button
            onClick={() => { setUploadFile(null); setUploadResult(null); setUpsertMode(false); setBulkUploadOpen(true); }}
            variant="outline"
            className="gap-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
            data-testid="open-bulk-upload-courses"
          >
            <Upload className="w-4 h-4" />
            Bulk Upload
          </Button>
          <Button 
            onClick={() => { resetForm(); setCourseDialogOpen(true); }} 
            className="bg-blue-600 hover:bg-blue-700 gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Course
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Courses</p>
              <p className="text-xl font-bold text-slate-900">{courses.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <Check className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Active</p>
              <p className="text-xl font-bold text-slate-900">
                {courses.filter(c => c.is_active).length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Professional</p>
              <p className="text-xl font-bold text-slate-900">
                {courses.filter(c => c.category === 'professional').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <IndianRupee className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Paid Courses</p>
              <p className="text-xl font-bold text-slate-900">
                {courses.filter(c => c.course_type === 'paid').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="flex-1 min-w-[200px] max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search courses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-4 py-2 border border-slate-200 rounded-lg text-sm"
        >
          <option value="all">All Categories</option>
          <option value="professional">Professional</option>
          <option value="distance_learning">Distance Learning</option>
          <option value="government">Government</option>
        </select>
        <label className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg cursor-pointer select-none hover:bg-slate-50" data-testid="select-all-courses">
          <input
            type="checkbox"
            checked={filteredCourses.length > 0 && filteredCourses.every(c => selectedIds.includes(c.id))}
            onChange={toggleSelectAllVisible}
            className="w-4 h-4 accent-blue-600"
          />
          Select All ({filteredCourses.length})
        </label>
      </div>

      {/* Bulk Action Toolbar (sticky, appears only when at least 1 course selected) */}
      {selectedIds.length > 0 && (
        <div className="sticky top-2 z-30 bg-blue-600 text-white rounded-xl shadow-lg p-3 flex flex-wrap items-center gap-3" data-testid="bulk-action-toolbar">
          <div className="flex items-center gap-2 px-2">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-white text-blue-700 font-bold text-sm">
              {selectedIds.length}
            </span>
            <span className="font-medium">course{selectedIds.length === 1 ? '' : 's'} selected</span>
          </div>
          <div className="flex flex-wrap gap-2 ml-auto">
            <button
              type="button"
              disabled={bulkBusy}
              onClick={() => setBulkConfirm('activate')}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50"
              data-testid="bulk-activate-btn"
            >
              <Check className="w-4 h-4" /> Activate
            </button>
            <button
              type="button"
              disabled={bulkBusy}
              onClick={() => setBulkConfirm('deactivate')}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium bg-amber-500 hover:bg-amber-600 disabled:opacity-50"
              data-testid="bulk-deactivate-btn"
            >
              <X className="w-4 h-4" /> Deactivate
            </button>
            <button
              type="button"
              disabled={bulkBusy}
              onClick={() => setBulkConfirm('delete')}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium bg-red-500 hover:bg-red-600 disabled:opacity-50"
              data-testid="bulk-delete-btn"
            >
              <Trash2 className="w-4 h-4" /> Delete
            </button>
            <button
              type="button"
              disabled={bulkBusy}
              onClick={clearSelection}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium bg-white/10 hover:bg-white/20"
              data-testid="bulk-clear-btn"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Courses Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredCourses.length === 0 ? (
          <div className="col-span-full text-center py-8 text-slate-500">
            No courses found
          </div>
        ) : (
          filteredCourses.map((course) => (
            <div
              key={course.id}
              className={`bg-white rounded-xl border overflow-hidden hover:shadow-lg transition-shadow relative ${
                selectedIds.includes(course.id)
                  ? 'border-blue-500 ring-2 ring-blue-200'
                  : 'border-slate-200'
              }`}
            >
              <label className="absolute top-3 left-3 z-10 flex items-center cursor-pointer bg-white/95 rounded p-1 border border-slate-200 hover:border-blue-400" title="Select for bulk action">
                <input
                  type="checkbox"
                  checked={selectedIds.includes(course.id)}
                  onChange={() => toggleSelectOne(course.id)}
                  className="w-4 h-4 accent-blue-600"
                  data-testid={`select-course-${course.id}`}
                />
              </label>
              <div className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    course.category === 'professional' ? 'bg-blue-100' :
                    course.category === 'distance_learning' ? 'bg-purple-100' :
                    'bg-emerald-100'
                  }`}>
                    <GraduationCap className={`w-5 h-5 ${
                      course.category === 'professional' ? 'text-blue-600' :
                      course.category === 'distance_learning' ? 'text-purple-600' :
                      'text-emerald-600'
                    }`} />
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleToggleActive(course)}
                      className={`group inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold transition-all border ${
                        course.is_active
                          ? 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100'
                          : 'bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200'
                      }`}
                      title={course.is_active ? 'Click to deactivate (hide from centers)' : 'Click to activate (show to centers)'}
                      data-testid={`toggle-active-${course.id}`}
                    >
                      <span
                        className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${
                          course.is_active ? 'bg-emerald-500' : 'bg-slate-400'
                        }`}
                      >
                        <span
                          className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform ${
                            course.is_active ? 'translate-x-3.5' : 'translate-x-0.5'
                          }`}
                        />
                      </span>
                      {course.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </div>
                </div>
                
                <h3 className="font-semibold text-slate-900 mb-2">{course.name}</h3>
                <p className="text-sm text-slate-500 line-clamp-2 mb-4">{course.description || 'No description'}</p>
                
                <div className="flex items-center gap-4 text-sm text-slate-600 mb-4">
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {course.duration || 'N/A'}
                  </div>
                  <div className="flex items-center gap-1">
                    <IndianRupee className="w-4 h-4" />
                    {course.fee_type === 'yearly' || course.yearly_fee ? (
                      <span className="text-purple-700">
                        ₹{Number(course.yearly_fee || course.fee).toLocaleString()}/yr
                        {course.total_fee && ` (Total: ₹${Number(course.total_fee).toLocaleString()})`}
                      </span>
                    ) : (
                      course.fee ? `₹${Number(course.fee).toLocaleString()}` : 'Free'
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    course.course_type === 'paid' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
                  }`}>
                    {course.course_type === 'paid' ? 'Paid' : 'Free'}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => openEditDialog(course)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => { setSelectedCourse(course); setDeleteConfirmOpen(true); }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Course Dialog */}
      <Dialog open={courseDialogOpen} onOpenChange={setCourseDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-blue-600" />
              {isEditing ? 'Edit Course' : 'Add New Course'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveCourse} className="space-y-4">
            <div>
              <Label>Course Name</Label>
              <Input
                value={courseForm.name}
                onChange={(e) => setCourseForm({...courseForm, name: e.target.value})}
                required
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <textarea
                value={courseForm.description}
                onChange={(e) => setCourseForm({...courseForm, description: e.target.value})}
                className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg resize-none"
                rows={3}
              />
            </div>

            {/* Duration & Fee - Conditional based on category */}
            {isDistanceLearning ? (
              <>
                {/* Distance Learning Fee Structure */}
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-4">
                  <h4 className="text-sm font-semibold text-purple-800 flex items-center gap-2">
                    <IndianRupee className="w-4 h-4" />
                    Distance Education Fee Structure (Yearly Basis)
                  </h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Duration (Years) / अवधि (वर्ष)</Label>
                      <select
                        value={courseForm.duration_years}
                        onChange={(e) => {
                          const years = e.target.value;
                          setCourseForm({
                            ...courseForm, 
                            duration_years: years,
                            duration: years ? `${years} Year${years > 1 ? 's' : ''}` : ''
                          });
                        }}
                        className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg"
                        required
                      >
                        <option value="">Select Years</option>
                        <option value="1">1 Year</option>
                        <option value="2">2 Years</option>
                        <option value="3">3 Years</option>
                        <option value="4">4 Years</option>
                        <option value="5">5 Years</option>
                      </select>
                    </div>
                    <div>
                      <Label>Yearly Fee (₹) / वार्षिक शुल्क</Label>
                      <Input
                        type="number"
                        value={courseForm.yearly_fee}
                        onChange={(e) => setCourseForm({...courseForm, yearly_fee: e.target.value})}
                        placeholder="e.g., 12000"
                        required
                      />
                    </div>
                  </div>
                  
                  {/* Auto-calculated Total Fee */}
                  <div className="bg-white rounded-lg p-3 border border-purple-200">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-purple-700 font-medium">Total Course Fee (Auto-Calculated)</span>
                      <span className="text-lg font-bold text-purple-900">
                        ₹{calculatedTotalFee.toLocaleString()}
                      </span>
                    </div>
                    <p className="text-xs text-purple-600 mt-1">
                      = {courseForm.duration_years || 0} Years × ₹{courseForm.yearly_fee || 0}/year
                    </p>
                  </div>
                  
                  <p className="text-xs text-purple-600 bg-purple-100 p-2 rounded">
                    💡 Distance Education courses mein students yearly installment mein fee pay kar sakte hain
                  </p>
                </div>
              </>
            ) : (
              <>
                {/* Regular Fee Structure */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Duration / अवधि</Label>
                    <Input
                      value={courseForm.duration}
                      onChange={(e) => setCourseForm({...courseForm, duration: e.target.value})}
                      placeholder="e.g., 3 Months"
                    />
                  </div>
                  <div>
                    <Label>Fee (₹) / शुल्क</Label>
                    <Input
                      type="number"
                      value={courseForm.fee}
                      onChange={(e) => setCourseForm({...courseForm, fee: e.target.value})}
                      placeholder="Total course fee"
                    />
                  </div>
                </div>
              </>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <select
                  value={courseForm.category}
                  onChange={(e) => setCourseForm({...courseForm, category: e.target.value})}
                  className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg"
                >
                  <option value="professional">Professional</option>
                  <option value="distance_learning">Distance Education</option>
                  <option value="degree">Degree Program</option>
                  <option value="diploma">Diploma Program</option>
                  <option value="government">Government</option>
                </select>
              </div>
              <div>
                <Label>Delivery Mode</Label>
                <select
                  value={courseForm.delivery_mode}
                  onChange={(e) => setCourseForm({...courseForm, delivery_mode: e.target.value})}
                  className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg"
                >
                  <option value="offline">Offline</option>
                  <option value="online">Online</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              </div>
            </div>

            {/* Training Mode — Professional category only (STT / RPL / Both) */}
            {courseForm.category === 'professional' && (
              <div>
                <Label>Select Training Mode <span className="text-red-500">*</span></Label>
                <select
                  value={courseForm.training_mode}
                  onChange={(e) => setCourseForm({...courseForm, training_mode: e.target.value})}
                  className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg"
                  data-testid="course-training-mode-select"
                >
                  <option value="Both">Both (STT &amp; RPL)</option>
                  <option value="STT">STT only</option>
                  <option value="RPL">RPL only</option>
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  Some Professional courses are available only via RPL — choose the applicable mode.
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Course Type</Label>
                <select
                  value={courseForm.course_type}
                  onChange={(e) => setCourseForm({...courseForm, course_type: e.target.value})}
                  className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg"
                >
                  <option value="paid">Paid</option>
                  <option value="free">Free</option>
                </select>
              </div>
              <div>
                <Label>Minimum Qualification</Label>
                <select
                  value={courseForm.minimum_qualification}
                  onChange={(e) => setCourseForm({...courseForm, minimum_qualification: e.target.value})}
                  className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg"
                >
                  <option value="5th">5th Pass</option>
                  <option value="8th">8th Pass</option>
                  <option value="10th">10th Pass</option>
                  <option value="12th">12th Pass</option>
                  <option value="graduation">Graduation</option>
                  <option value="post_graduation">Post Graduation</option>
                </select>
              </div>
            </div>

            <div>
              <Label>Eligibility Details</Label>
              <Input
                value={courseForm.eligibility}
                onChange={(e) => setCourseForm({...courseForm, eligibility: e.target.value})}
                placeholder="Any specific eligibility criteria"
              />
            </div>

            {/* Subjects for Marksheet */}
            <div className="border border-blue-200 rounded-lg p-4 bg-blue-50/50">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-blue-800 flex items-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  Subjects / विषय (Marksheet & Question Bank)
                </h4>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="text-blue-600 border-blue-300 h-7 text-xs"
                  onClick={() => setCourseForm({
                    ...courseForm,
                    subjects: [...(courseForm.subjects || []), { subject_code: '', subject_title: '', subject_type: 'Core' }]
                  })}
                >
                  <Plus className="w-3 h-3 mr-1" /> Add Subject
                </Button>
              </div>
              
              {(!courseForm.subjects || courseForm.subjects.length === 0) ? (
                <p className="text-xs text-slate-500 text-center py-3">No subjects added. Add subjects for marksheet & question bank mapping.</p>
              ) : (
                <div className="space-y-2">
                  {courseForm.subjects.map((subj, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-end bg-white p-2 rounded border border-slate-200">
                      <div className="col-span-3">
                        {idx === 0 && <Label className="text-xs text-slate-500 mb-1 block">Code</Label>}
                        <Input
                          value={subj.subject_code}
                          onChange={(e) => {
                            const updated = [...courseForm.subjects];
                            updated[idx] = { ...updated[idx], subject_code: e.target.value.toUpperCase() };
                            setCourseForm({ ...courseForm, subjects: updated });
                          }}
                          placeholder="NOS-001"
                          className="h-8 text-xs font-mono"
                          data-testid={`subject-code-${idx}`}
                        />
                      </div>
                      <div className="col-span-5">
                        {idx === 0 && <Label className="text-xs text-slate-500 mb-1 block">Title</Label>}
                        <Input
                          value={subj.subject_title}
                          onChange={(e) => {
                            const updated = [...courseForm.subjects];
                            updated[idx] = { ...updated[idx], subject_title: e.target.value };
                            setCourseForm({ ...courseForm, subjects: updated });
                          }}
                          placeholder="Subject Title"
                          className="h-8 text-xs"
                          data-testid={`subject-title-${idx}`}
                        />
                      </div>
                      <div className="col-span-3">
                        {idx === 0 && <Label className="text-xs text-slate-500 mb-1 block">Type</Label>}
                        <select
                          value={subj.subject_type}
                          onChange={(e) => {
                            const updated = [...courseForm.subjects];
                            updated[idx] = { ...updated[idx], subject_type: e.target.value };
                            setCourseForm({ ...courseForm, subjects: updated });
                          }}
                          className="w-full h-8 px-2 text-xs border border-slate-200 rounded-md"
                          data-testid={`subject-type-${idx}`}
                        >
                          <option value="Core">Core</option>
                          <option value="Non-Core">Non-Core</option>
                          <option value="Elective">Elective</option>
                          <option value="Practical">Practical</option>
                        </select>
                      </div>
                      <div className="col-span-1 flex justify-center">
                        <button
                          type="button"
                          onClick={() => {
                            const updated = courseForm.subjects.filter((_, i) => i !== idx);
                            setCourseForm({ ...courseForm, subjects: updated });
                          }}
                          className="text-red-400 hover:text-red-600 p-1"
                          data-testid={`remove-subject-${idx}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={courseForm.is_active}
                onChange={(e) => setCourseForm({...courseForm, is_active: e.target.checked})}
                className="rounded"
              />
              <Label htmlFor="is_active" className="cursor-pointer">Course is active</Label>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setCourseDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving} className="bg-blue-600 hover:bg-blue-700">
                {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                {isEditing ? 'Update Course' : 'Create Course'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Confirm Delete
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600">Are you sure you want to delete this course?</p>
            <p className="text-red-500 text-sm mt-2">Course: {selectedCourse?.name}</p>
            <p className="text-slate-400 text-xs mt-2">This action cannot be undone.</p>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleDeleteCourse} className="bg-red-600 hover:bg-red-700">
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk CSV/Excel Upload */}
      <Dialog open={bulkUploadOpen} onOpenChange={(o) => { if (!uploading) setBulkUploadOpen(o); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <FileSpreadsheet className="w-5 h-5" />
              Bulk Upload Courses
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900">
              <p className="font-semibold mb-1">How it works</p>
              <ol className="list-decimal pl-5 space-y-0.5 text-blue-800">
                <li>Download the CSV template (sample rows included), OR click <strong>Export</strong> to bulk-edit existing courses</li>
                <li>Fill / edit columns: <code>name</code>, <code>category</code> are required for new rows</li>
                <li>For Distance/Degree/Diploma rows fill <code>yearly_fee</code> + <code>duration_years</code></li>
                <li>Tick <strong>Update existing courses</strong> below if you're re-uploading an Export</li>
              </ol>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => downloadTemplate('csv')}
                className="gap-1"
                data-testid="download-courses-template-csv"
              >
                <Download className="w-4 h-4" /> CSV Template
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => downloadTemplate('xlsx')}
                className="gap-1"
                data-testid="download-courses-template-xlsx"
              >
                <Download className="w-4 h-4" /> Excel Template
              </Button>
            </div>

            <div>
              <Label htmlFor="bulk-courses-file" className="mb-2 block">Select File (.csv / .xlsx)</Label>
              <input
                id="bulk-courses-file"
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => { setUploadFile(e.target.files?.[0] || null); setUploadResult(null); }}
                className="block w-full text-sm text-slate-700 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-emerald-100 file:text-emerald-700 hover:file:bg-emerald-200"
                data-testid="bulk-courses-file-input"
              />
              {uploadFile && (
                <p className="text-xs text-slate-500 mt-2">
                  {uploadFile.name} • {(uploadFile.size / 1024).toFixed(1)} KB
                </p>
              )}
            </div>

            {/* Upsert toggle — bulk-edit support */}
            <label className="flex items-start gap-2 p-3 rounded-lg border border-purple-200 bg-purple-50 cursor-pointer">
              <input
                type="checkbox"
                checked={upsertMode}
                onChange={(e) => setUpsertMode(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-purple-600"
                data-testid="upsert-mode-toggle"
              />
              <div className="text-sm">
                <p className="font-semibold text-purple-900">Update existing courses (instead of skip)</p>
                <p className="text-purple-700 text-xs mt-0.5">
                  Match by <code>id</code> first, then by <code>name</code>. Use this with the
                  <strong> Export</strong> file to bulk-edit fees, descriptions, or any field.
                </p>
              </div>
            </label>

            {uploadResult && (
              <div className={`rounded-lg p-3 border text-sm ${
                ((uploadResult.added || 0) + (uploadResult.updated || 0)) > 0
                  ? 'bg-emerald-50 border-emerald-200'
                  : 'bg-amber-50 border-amber-200'
              }`}>
                <p className="font-semibold">
                  Added: {uploadResult.added || 0}
                  {typeof uploadResult.updated === 'number' && ` • Updated: ${uploadResult.updated}`}
                  {' '}• Skipped: {uploadResult.skipped || 0}
                </p>
                {uploadResult.errors && uploadResult.errors.length > 0 && (
                  <ul className="mt-2 max-h-32 overflow-y-auto text-xs text-slate-700 list-disc pl-4">
                    {uploadResult.errors.slice(0, 10).map((e, i) => <li key={i}>{e}</li>)}
                    {uploadResult.errors.length > 10 && (
                      <li className="text-slate-500">…and {uploadResult.errors.length - 10} more</li>
                    )}
                  </ul>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setBulkUploadOpen(false)} disabled={uploading}>
              Close
            </Button>
            <Button
              onClick={handleBulkUpload}
              disabled={uploading || !uploadFile}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-bulk-courses-upload"
            >
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {uploading ? 'Uploading…' : 'Upload'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Action Confirmation */}
      <Dialog open={!!bulkConfirm} onOpenChange={(o) => !o && setBulkConfirm(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${
              bulkConfirm === 'delete' ? 'text-red-600' :
              bulkConfirm === 'activate' ? 'text-emerald-600' : 'text-amber-600'
            }`}>
              {bulkConfirm === 'delete' && <Trash2 className="w-5 h-5" />}
              {bulkConfirm === 'activate' && <Check className="w-5 h-5" />}
              {bulkConfirm === 'deactivate' && <X className="w-5 h-5" />}
              Bulk {bulkConfirm === 'activate' ? 'Activate' : bulkConfirm === 'deactivate' ? 'Deactivate' : 'Delete'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 text-sm">
            <p className="text-slate-600">
              {bulkConfirm === 'delete'
                ? 'Are you sure you want to permanently delete the selected courses? This action cannot be undone.'
                : bulkConfirm === 'activate'
                ? 'These courses will become visible to all centers and students.'
                : 'These courses will be hidden from all centers and students.'}
            </p>
            <p className="text-slate-900 font-semibold mt-3">{selectedIds.length} course{selectedIds.length === 1 ? '' : 's'}</p>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setBulkConfirm(null)} disabled={bulkBusy}>
              Cancel
            </Button>
            <Button
              onClick={() => runBulkAction(bulkConfirm)}
              disabled={bulkBusy}
              className={
                bulkConfirm === 'delete' ? 'bg-red-600 hover:bg-red-700' :
                bulkConfirm === 'activate' ? 'bg-emerald-600 hover:bg-emerald-700' :
                'bg-amber-600 hover:bg-amber-700'
              }
              data-testid="confirm-bulk-action-btn"
            >
              {bulkBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Confirm
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCoursesPage;
